import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { AuthService } from "src/app/core/core-services/services/auth.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { forkJoin, Observable, of, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { getPlanId } from "src/app/core/core-services/constants/plans.constants";
import { userHasSentenceRewriterAccess } from "src/app/core/models/user.model";
import { ScrollService } from "src/app/core/core-services/services/scroll.service";
import { ExtensionSettingsService } from "./extension-settings.service";
import { ExtensionPlan, ExtensionSettingsData, GrammarLanguage } from "./extension-settings.models";
import { FlygrammarSettingsComponent } from "./flygrammar-settings/flygrammar-settings.component";
import { FlyrewriteSettingsComponent } from "./flyrewrite-settings/flyrewrite-settings.component";
import { SentenceRewriteSettingsComponent } from "./sentence-rewrite-settings/sentence-rewrite-settings.component";
import { TypingStyleSettingsComponent } from "./typing-style-settings/typing-style-settings.component";
import { FlycutTimeoutSettingsComponent } from "./flycut-timeout-settings/flycut-timeout-settings.component";
import { DisabledSitesSettingsComponent } from "./disabled-sites-settings/disabled-sites-settings.component";
import { CsvSeparatorSettingsComponent } from "./csv-separator-settings/csv-separator-settings.component";
import {
  UnsavedChangesAction,
  UnsavedChangesDialogComponent,
} from "./unsaved-changes-dialog/unsaved-changes-dialog.component";

interface DirtySectionInfo {
  dirty: boolean;
  component: any;
}

@Component({
    selector: "app-extension-settings",
    templateUrl: "./extension-settings.component.html",
    styleUrls: ["./extension-settings.component.scss"],
    standalone: false
})
export class ExtensionSettingsComponent implements OnInit, OnDestroy {
  @Input() settingsData: ExtensionSettingsData;
  @Input() planId: string;

  @ViewChild("grammarCard") grammarCard: FlygrammarSettingsComponent;
  @ViewChild("rewriteCard") rewriteCard: FlyrewriteSettingsComponent;
  @ViewChild("sentenceRewriteCard") sentenceRewriteCard: SentenceRewriteSettingsComponent;
  @ViewChild("typingCard") typingCard: TypingStyleSettingsComponent;
  @ViewChild("timeoutCard") timeoutCard: FlycutTimeoutSettingsComponent;
  @ViewChild("disabledSitesCard") disabledSitesCard: DisabledSitesSettingsComponent;
  @ViewChild("csvSeparatorCard") csvSeparatorCard: CsvSeparatorSettingsComponent;

  // Extension detection
  isInstalled = false;
  version: number;
  isChromeBrowser = false;

  // FlyGrammar
  flyGrammarEnabled = true;
  flyGrammarLanguage = "";
  flyGrammarOverrideUser = false;
  flyGrammarBlockedDomains: string[] = [];
  flyGrammarLanguagesAvailable: GrammarLanguage[] = [
    { id: "", value: "Select a default language" },
    { id: "en-US", value: "English (US)" },
  ];
  canDisableFlyGrammar = true;

  // FlyRewrite
  flyRewriteEnabled = true;
  flyRewriteOverrideUser = false;
  flyRewriteBlockedDomains: string[] = [];

  // Sentence Rewriter (automatic AI scoring — default OFF, opt-in consent contract)
  sentenceRewriteEnabled = false;
  sentenceRewriteOverrideUser = false;
  sentenceRewriteBlockedDomains: string[] = [];

  // Typing Style
  typingStyle: "letter" | "all" = "letter";
  typingStyleOverrideUser = false;
  typingSpeed = 45;

  // Timeout
  timeout = 5000;

  // Disabled sites (Shortcuts list — the field the old Domain Blacklist edited)
  blockedDomains: string[] = [];
  pluginBlockedDomains: string[] = [];

  // CSV Split Separator
  csvSplitSeparator: string | null = null;

  // Meta
  plan: ExtensionPlan = null;
  initialLoading = true;

  // Dirty tracking
  private dirtySections = new Map<string, DirtySectionInfo>();
  private destroy$ = new Subject<void>();
  private eventListener: any;

  constructor(
    private readonly settingsService: ExtensionSettingsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: DialogService,
    private readonly authService: AuthService,
    public scrollService: ScrollService
  ) {}

  /**
   * Issue #561's mandatory product gate: the Sentence Rewriter card and its
   * Disabled Sites column render only for users entitled via the per-user
   * feature map's `sentence_rewriter` key. See
   * `userHasSentenceRewriterAccess`'s JSDoc — this is the single source of
   * truth for the gate, not a locally re-read flag.
   */
  get sentenceRewriterAccessGranted(): boolean {
    return userHasSentenceRewriterAccess(this.authService.userDetails?.user_details);
  }

  ngOnInit(): void {
    this.isChromeBrowser = this.settingsService.isChromeBrowser;
    this.planId = this.planId || getPlanId();
    this.loadSettingsFromData();
    this.checkExtension();
    this.loadMetaData();
    this.setupExtensionEventListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.eventListener) {
      window.removeEventListener("fmsUpdateSettingsFE", this.eventListener);
    }
  }

  private loadSettingsFromData(): void {
    if (!this.settingsData) return;

    const data = this.settingsData;

    this.timeout = data.shortcut_timeout || 5000;
    this.typingSpeed = data.typing_speed || 45;
    this.typingStyle = (data.typing_style as "letter" | "all") || "letter";
    this.blockedDomains = data.blocked_domains || [];
    this.pluginBlockedDomains = data.plugin_blocked_domains || [];
    this.csvSplitSeparator = data.csv_split_separator ?? null;

    if (data.fly_grammar !== undefined) {
      this.flyGrammarEnabled = data.fly_grammar === "enabled";
    }
    this.flyGrammarLanguage = data.fly_grammar_default_language || "";
    this.flyGrammarBlockedDomains = data.fly_grammar_blocked_domains ?? [];
    this.flyRewriteBlockedDomains = data.fly_rewrite_blocked_domains ?? [];
    this.sentenceRewriteBlockedDomains = data.fly_sentence_rewrite_blocked_domains ?? [];

    if (data.fly_rewrite !== undefined) {
      this.flyRewriteEnabled = data.fly_rewrite === "enabled";
    }

    // Sentence Rewriter defaults to disabled (opt-in) — an absent key must
    // never read as enabled, unlike the other feature toggles.
    if (data.fly_sentence_rewrite !== undefined) {
      this.sentenceRewriteEnabled = data.fly_sentence_rewrite === "enabled";
    }

    // Company overrides
    if (data.company?.override_user_typing_style) {
      this.typingStyleOverrideUser = true;
      if (data.company.typing_style !== undefined) {
        this.typingStyle = data.company.typing_style as "letter" | "all";
      }
      if (data.company.typing_speed !== undefined) {
        this.typingSpeed = data.company.typing_speed;
      }
    }

    if (data.company?.override_user_flygrammar) {
      this.flyGrammarOverrideUser = true;
      if (data.company.fly_grammar !== undefined) {
        this.flyGrammarEnabled = data.company.fly_grammar === "enabled";
      }
      if (data.company.fly_grammar_default_language !== undefined) {
        this.flyGrammarLanguage = data.company.fly_grammar_default_language;
      }
    }

    if (data.company?.override_user_flyrewrite) {
      this.flyRewriteOverrideUser = true;
      if (data.company.fly_rewrite !== undefined) {
        this.flyRewriteEnabled = data.company.fly_rewrite === "enabled";
      }
    }

    if (data.company?.override_user_fly_sentence_rewrite) {
      this.sentenceRewriteOverrideUser = true;
      if (data.company.fly_sentence_rewrite !== undefined) {
        this.sentenceRewriteEnabled = data.company.fly_sentence_rewrite === "enabled";
      }
    }
  }

  private async checkExtension(): Promise<void> {
    this.isInstalled = await this.settingsService.checkExtensionInstalled();
    if (this.isInstalled) {
      this.version = await this.settingsService.getExtensionVersion();
    }
    this.cdr.detectChanges();
  }

  private loadMetaData(): void {
    this.settingsService.loadMetaData().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.plan = result.plan;
        this.canDisableFlyGrammar = result.plan?.can_disable_flygrammar ?? true;
        if (result.fly_grammar_languages_available) {
          this.flyGrammarLanguagesAvailable = [
            { id: "", value: "Select a default language" },
            ...result.fly_grammar_languages_available,
          ];
        }
      }
      this.initialLoading = false;
      this.cdr.detectChanges();
    });
  }

  private setupExtensionEventListener(): void {
    this.eventListener = (event: CustomEvent) => {
      const detail = event.detail;
      if (detail.timeout) this.timeout = detail.timeout;
      if (detail.typing_speed) this.typingSpeed = detail.typing_speed;
      if (detail.typing_style) this.typingStyle = detail.typing_style;

      if (detail.settings) {
        const s = detail.settings;
        this.timeout = s.shortcut_timeout;
        this.typingSpeed = s.typing_speed;
        this.typingStyle = s.typing_style;
        if (s.fly_grammar !== undefined) {
          this.flyGrammarEnabled = s.fly_grammar === "enabled";
        }
        this.flyGrammarLanguage = s.fly_grammar_default_language;
        this.flyGrammarBlockedDomains = s.fly_grammar_blocked_domains;
        this.flyRewriteBlockedDomains = s.fly_rewrite_blocked_domains;
        if (s.fly_sentence_rewrite !== undefined) {
          this.sentenceRewriteEnabled = s.fly_sentence_rewrite === "enabled";
        }
        if (s.fly_sentence_rewrite_blocked_domains !== undefined) {
          this.sentenceRewriteBlockedDomains = s.fly_sentence_rewrite_blocked_domains ?? [];
        }
        // All five per-site lists feed the Disabled Sites manager, so a change
        // made in the extension popup lands here on the next update ping.
        if (s.blocked_domains !== undefined) {
          this.blockedDomains = s.blocked_domains ?? [];
        }
        if (s.plugin_blocked_domains !== undefined) {
          this.pluginBlockedDomains = s.plugin_blocked_domains ?? [];
        }
        if (s.csv_split_separator !== undefined) {
          this.csvSplitSeparator = s.csv_split_separator ?? null;
        }
      }

      this.cdr.detectChanges();
    };

    window.addEventListener("fmsUpdateSettingsFE", this.eventListener);
  }

  // Dirty state tracking
  onDirtyChange(sectionName: string, isDirty: boolean, component: any): void {
    this.dirtySections.set(sectionName, { dirty: isDirty, component });
  }

  hasDirtySection(): boolean {
    for (const [, info] of this.dirtySections) {
      if (info.dirty) return true;
    }
    return false;
  }

  getDirtySectionNames(): string[] {
    const names: string[] = [];
    for (const [name, info] of this.dirtySections) {
      if (info.dirty) names.push(name);
    }
    return names;
  }

  promptUnsavedChanges(): Observable<UnsavedChangesAction> {
    const dirtySections = this.getDirtySectionNames();
    if (!dirtySections.length) return of("discard" as UnsavedChangesAction);

    // CLAUDE.md (Shared Building Blocks -> Dialogs): DialogService is the only
    // sanctioned entry point — never call VngDialogService.open directly.
    // Width stays the literal "480px" (DIALOG_WIDTHS.md is "500px").
    const dialogRef = this.dialog.openDialogComponent<UnsavedChangesAction>(
      UnsavedChangesDialogComponent,
      { dirtySections },
      "480px"
    );

    return dialogRef.afterClosed().pipe(
      map((result: UnsavedChangesAction | undefined) => result || "cancel")
    );
  }

  saveAllDirty(): Observable<void> {
    const saveOps: Observable<any>[] = [];
    for (const [, info] of this.dirtySections) {
      if (info.dirty && info.component) {
        saveOps.push(info.component.getSaveObservable());
      }
    }
    if (!saveOps.length) return of(void 0);
    return forkJoin(saveOps).pipe(map(() => void 0));
  }

  discardAllDirty(): void {
    for (const [, info] of this.dirtySections) {
      if (info.dirty && info.component) {
        info.component.discardChanges();
      }
    }
  }

  // CanDeactivate support
  canDeactivate(): boolean {
    return !this.hasDirtySection();
  }
}
